import axios, { AxiosRequestConfig } from 'axios';
import axiosRetry, { isRetryableError } from 'axios-retry';
import _ from 'lodash';
import { ErrorMessages } from 'enums';
import useUserStore from 'stores/user-store';
import config from '../../config';
import { refreshTokenCall, scheduleProactiveRefresh } from './token-refresh';

const API_V1 = axios.create({
  baseURL: _.get(config, 'apiBaseUrl'),
  responseType: 'json',
});

axiosRetry(API_V1, {
  retries: 2,
  retryDelay: (retryCount) => {
    return retryCount * 1000;
  },
  retryCondition: isRetryableError,
});

export const isTokenError = (message: string | undefined) => {
  if (!message) return false;
  const tokenErrors = ['Token expired', 'jwt expired', 'jwt malformed', 'Invalid or expired token'];

  return tokenErrors.includes(message);
};

const isAccountDisabledError = (message: string | undefined) => message === 'Account is disabled';

// Helper to extract error message from various response formats
export const getErrorMessage = (data: any): string | undefined => {
  // Handle { message: "..." } format
  if (data?.message) return data.message;
  // Handle { error: { message: "..." } } format
  if (data?.error?.message) return data.error.message;
  if (typeof data?.error === 'string') return data.error;
  return undefined;
};

export const LINKEDIN_SESSION_INVALID_EVENT = 'linkedin-session-invalid';

const sessionInvalidDispatchCache: Record<string, number> = {};
const sessionInvalidDedupeWindowMs = 5000;

/** API JSON body or SSE error payload: treat as “needs LinkedIn reconnect”, not a generic bug. */
export function isLinkedinSessionInvalidApiError(data: any): boolean {
  if (!data || typeof data !== 'object') return false;
  if (data.code === 'SESSION_INVALID' || data.code === 'ACCOUNT_OUT_OF_SYNC') return true;
  if (data.error === 'SESSION_INVALID' || data.error === 'ACCOUNT_OUT_OF_SYNC') return true;
  const rawMsg = getErrorMessage(data);
  const msg = (typeof rawMsg === 'string' ? rawMsg : '').toLowerCase();
  const legacyOutOfSync = ErrorMessages.ACCOUNT_OUT_OF_SYNC.toLowerCase();
  if (msg === legacyOutOfSync) return true;
  if (msg.includes('linkedin session expired')) return true;
  if (msg.includes('re-sync your linkedin')) return true;
  if (msg.includes('linkedin session not available')) return true;
  if (msg.includes('session is being refreshed')) return true;
  if (msg.includes('login again to continue')) return true;
  if (msg.includes('linkedin ended this session')) return true;
  if (msg.includes('linkedin has signed this account out')) return true;
  return false;
}

export function fireLinkedinSessionInvalid(
  accountId: string,
  detail: { code?: string; message?: string } = {},
  dedupeKey = 'default',
): void {
  if (typeof window === 'undefined' || !accountId) return;
  const cacheKey = `${accountId}:${dedupeKey}`;
  const now = Date.now();
  if (sessionInvalidDispatchCache[cacheKey] && now - sessionInvalidDispatchCache[cacheKey] < sessionInvalidDedupeWindowMs) {
    return;
  }
  sessionInvalidDispatchCache[cacheKey] = now;
  window.dispatchEvent(
    new CustomEvent(LINKEDIN_SESSION_INVALID_EVENT, {
      detail: {
        accountId,
        code: detail.code,
        message: detail.message,
      },
    }),
  );
}

const tryParseJson = (value: unknown): any => {
  if (typeof value !== 'string') {
    return value;
  }
  try {
    return JSON.parse(value);
  } catch {
    return undefined;
  }
};

const extractLinkedinAccountIdFromRequest = (request: AxiosRequestConfig<any>): string | null => {
  const data = tryParseJson(request?.data);
  const params = request?.params || {};

  const directAccountId =
    data?.linkedinAccount ||
    data?.linkedinAccountId ||
    data?.accountId ||
    params?.linkedinAccount ||
    params?.linkedinAccountId ||
    params?.accountId;

  if (typeof directAccountId === 'string' && directAccountId !== '') {
    return directAccountId;
  }

  if (typeof request?.url === 'string') {
    const match = request.url.match(/\/linkedinAccounts\/([^/?]+)/);
    if (match?.[1]) {
      return match[1];
    }
  }

  return null;
};

const dispatchLinkedinSessionInvalidEvent = (error: any) => {
  const errorData = error?.response?.data;
  if (!isLinkedinSessionInvalidApiError(errorData) || typeof window === 'undefined') {
    return;
  }

  const accountId =
    extractLinkedinAccountIdFromRequest(error?.config || {}) ||
    (typeof errorData?.linkedinAccountId === 'string' ? errorData.linkedinAccountId : null);
  if (!accountId) {
    return;
  }

  const msg = getErrorMessage(errorData) || (typeof errorData?.error === 'string' ? errorData.error : undefined);
  const codeStr =
    errorData?.code !== undefined && errorData?.code !== null && errorData?.code !== ''
      ? String(errorData.code)
      : typeof errorData?.error === 'string'
        ? errorData.error
        : undefined;
  fireLinkedinSessionInvalid(accountId, { code: codeStr, message: msg }, error?.config?.url || 'axios');
};

// REQUEST interceptor: Auto-inject authentication token
API_V1.interceptors.request.use((config) => {
  const token = useUserStore.getState().token?.accessToken;
  if (token && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// =============================================================================
// Mutex for reactive (401-triggered) refresh
//
// When multiple requests fail with 401 simultaneously, only one refresh call is
// made. All others await the same promise and then retry with the new token.
// This is the safety net; the proactive timer in token-refresh.ts handles the
// normal case so users never see a 401.
// =============================================================================
let refreshPromise: Promise<any> | null = null;

// RESPONSE interceptor: Handle token refresh (safety net)
API_V1.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalReq = error.config;
    const userStore = useUserStore.getState();
    const togglePrompt = userStore.togglePrompt;

    const errorMessage = getErrorMessage(error.response?.data);
    if (!_.isNil(error.response) && error.response.status === 403 && isAccountDisabledError(errorMessage)) {
      userStore.signOut();
      togglePrompt(true);
      return Promise.reject(error);
    }

    if (!_.isNil(error.response) && error.response.status === 401 && isTokenError(errorMessage) && !originalReq._retry) {
      originalReq._retry = true;

      if (!refreshPromise) {
        refreshPromise = refreshTokenCall().finally(() => {
          refreshPromise = null;
        });
      }

      const resToken = await refreshPromise;

      if (!resToken.error) {
        scheduleProactiveRefresh(resToken.expiresIn);

        const newToken = resToken.accessToken || useUserStore.getState().token?.accessToken;
        originalReq.headers.Authorization = `Bearer ${newToken}`;

        return API_V1(originalReq);
      } else {
        togglePrompt(true);
      }
    }

    dispatchLinkedinSessionInvalidEvent(error);

    return Promise.reject(error);
  }
);

// Re-export for backwards compatibility (consumers import from APIv1)
export { refreshTokenCall } from './token-refresh';

export { API_V1 };
export default axios.create({
  baseURL: _.get(config, 'apiBaseUrl'),
  responseType: 'json',
});
